import {
  CalendarMockup, ContractMockup, InspectionMockup, VehicleAddMockup,
  ClientsMockup, PaymentMockup, ManagementMockup, SignatureMockup, CopilotMockup,
  RevenueMockup,
} from "../components/BecomeHostMockups";
import {
  CalendarDays, LayoutGrid, ClipboardList, FileText, Camera, Wrench, Users,
  CreditCard, Settings2, Zap, BellRing, ShieldCheck, PenLine, Search, Clock,
  TrendingUp, RefreshCw, FolderCheck, Palette, AlarmClock, Banknote, Lock,
  History, Filter, Sparkles, FileCheck, Globe, Package, Receipt, MailCheck, UserCog,
} from "lucide-react";

export const FEATURE_PAGES = [
  {
    slug: "pilotage-calendrier",
    icon: CalendarDays,
    menuLabel: "Pilotage & calendrier",
    seoTitle: "Planning de location de véhicules | Junago",
    overline: "Pilotage & calendrier",
    title: "Toute votre flotte, sur un seul calendrier.",
    subtitle:
      "Le Pilotage Junago centralise réservations, tâches et véhicules dans un calendrier interactif façon Google Calendar. Votre journée de loueur commence ici.",
    Mockup: CalendarMockup,
    helpSlug: "prise-en-main-pilotage",
    benefits: [
      { icon: CalendarDays, title: "Vue jour, semaine, mois", desc: "Chaque véhicule a sa ligne, chaque réservation son bloc coloré. Zoomez du planning global au détail d'une journée." },
      { icon: Zap, title: "Tâches générées automatiquement", desc: "Contrat à signer, état des lieux, restitution : les actions apparaissent d'elles-mêmes au bon moment." },
      { icon: ShieldCheck, title: "Conflits de dates détectés", desc: "Impossible de louer deux fois le même véhicule : le calendrier bloque les chevauchements." },
      { icon: LayoutGrid, title: "Tout en 2 menus", desc: "Contrairement aux outils à 15 menus, tout Junago tient dans Pilotage + Paramétrages." },
    ],
    quote: { text: "Le calendrier a remplacé mon tableau Excel et mes trois carnets. Je vois tout, tout de suite.", author: "Marc D.", role: "Loueur indépendant, Paris" },
  },
  {
    slug: "assistant-ia",
    icon: Sparkles,
    menuLabel: "Junago Copilot (IA)",
    seoTitle: "Assistant IA pour loueurs de véhicules — Junago Copilot",
    overline: "Junago Copilot · Assistant IA",
    title: "Votre activité, décryptée par l'IA.",
    subtitle:
      "Junago Copilot analyse vos réservations et vos revenus, puis vous explique ce qui marche : véhicules les plus loués, fréquences de réservation, tendances. Un assistant qui informe — les décisions restent les vôtres.",
    Mockup: CopilotMockup,
    helpSlug: "junago-copilot-assistant-ia",
    benefits: [
      { icon: FileCheck, title: "Rapports automatiques", desc: "Un rapport d'activité clair, généré à la demande : résumé, chiffres clés, tendances — en français, sans jargon." },
      { icon: TrendingUp, title: "Véhicules stars identifiés", desc: "Copilot repère vos véhicules les plus rentables et ceux qui dorment, avec une explication pour chacun." },
      { icon: CalendarDays, title: "Fréquences de réservation", desc: "Jours populaires, saisonnalité, durées moyennes : comprenez quand et comment vos clients réservent." },
      { icon: Sparkles, title: "Recommandations concrètes", desc: "Des suggestions actionnables (« votre SUV part 9 week-ends sur 10, ajustez le tarif du samedi »). Pas un agent : un éclaireur." },
    ],
    quote: { text: "Copilot m'a montré que mes utilitaires partaient surtout le lundi. J'ai adapté mes tarifs, le CA a suivi.", author: "Éric T.", role: "Loueur utilitaires, Lille" },
  },
  {
    slug: "page-de-reservation",
    icon: Globe,
    menuLabel: "Site de réservation en ligne",
    seoTitle: "Votre site internet de réservation de voitures, généré et hébergé | Junago",
    overline: "Votre site internet de réservation",
    title: "Un vrai site internet professionnel, connecté à votre logiciel.",
    subtitle:
      "Junago génère et héberge votre site internet de réservation en ligne : design premium responsive, disponibilités en temps réel, paiement sécurisé, SEO optimisé. Vos véhicules, tarifs et options s'y synchronisent automatiquement — aucune double saisie, réservations 24h/24.",
    Mockup: PaymentMockup,
    helpSlug: "votre-page-de-reservation-en-ligne",
    benefits: [
      { icon: Globe, title: "Hébergé et sécurisé par Junago", desc: "Hébergement inclus, certificat SSL, adresse Junago par défaut. 4 designs premium responsive, sans aucune mention Junago visible : c'est votre site." },
      { icon: RefreshCw, title: "Synchronisé en temps réel", desc: "Véhicules, disponibilités, tarifs saisonniers, options et accessoires, agences : chaque modification dans votre logiciel est répercutée immédiatement sur le site. Zéro double saisie." },
      { icon: Lock, title: "Réservation & paiement 24h/24", desc: "Vos clients réservent à toute heure et paient en ligne — acompte ou paiement intégral, caution selon le mode choisi. Stripe, SSL, 3D Secure : la réservation tombe dans votre Pilotage." },
      { icon: Search, title: "Pages véhicules & SEO automatiques", desc: "Chaque véhicule a sa page générée automatiquement, avec filtres de recherche et référencement optimisé. Badge « Entreprise vérifiée » pour convertir davantage." },
    ],
    quote: { text: "J'ai activé mon site un lundi matin, j'avais ma première réservation en ligne le soir même.", author: "Sonia B.", role: "Loueuse indépendante, Guadeloupe" },
  },
  {
    slug: "produits-options",
    icon: Package,
    menuLabel: "Produits & options",
    seoTitle: "Vendez des options et accessoires de location : sièges bébé, assurances, livraison | Junago",
    overline: "Produits & options",
    title: "Augmentez votre panier moyen à chaque réservation.",
    subtitle:
      "Sièges bébé, rachat de franchise, livraison, nettoyage premium… Créez vos options en quelques clics : elles sont proposées automatiquement sur votre site de réservation et dans vos devis, avec un stock maîtrisé par agence.",
    Mockup: VehicleAddMockup,
    helpSlug: "produits-options-extras",
    benefits: [
      { icon: Package, title: "Catalogue d'extras illimité", desc: "Options par jour ou par réservation, produits physiques ou services : vous fixez le prix, la TVA et les règles d'affichage." },
      { icon: ShieldCheck, title: "Stock strict ou souple", desc: "Stock limité (5 sièges bébé) ou illimité (assurance) : Junago bloque la survente en mode strict et suit la disponibilité en temps réel." },
      { icon: RefreshCw, title: "Stock par agence", desc: "Répartissez vos accessoires entre vos agences avec repli automatique sur le stock global : chaque site vend ce qu'il a réellement." },
      { icon: TrendingUp, title: "Rentabilité par produit", desc: "Chiffre d'affaires, marge et volume par option : identifiez ce qui se vend et ajustez votre catalogue." },
    ],
    quote: { text: "Le rachat de franchise à 12€/jour, c'est 30% de mes marges maintenant. Avant Junago je ne le proposais même pas.", author: "Karim L.", role: "Gérant, agence multi-sites" },
  },
  {
    slug: "reservations-devis",
    icon: ClipboardList,
    menuLabel: "Réservations & devis",
    seoTitle: "Logiciel de gestion des réservations et devis | Junago",
    overline: "Réservations & devis",
    title: "Du devis à la restitution, sans friction.",
    subtitle:
      "Créez une réservation en 30 secondes, envoyez des devis professionnels et suivez chaque location étape par étape, du premier contact à la remise des clés.",
    Mockup: ManagementMockup,
    helpSlug: "cycle-de-vie-reservation",
    benefits: [
      { icon: ClipboardList, title: "Réservations en 30 secondes", desc: "Téléphone, agence, e-mail : enregistrez toute demande via le bouton « + », où que vous soyez dans le back office." },
      { icon: FileCheck, title: "Devis transformés en 1 clic", desc: "Un devis accepté devient une réservation confirmée, sans ressaisie. Suivi des statuts : envoyé, accepté, expiré." },
      { icon: RefreshCw, title: "Cycle de vie structuré", desc: "Devis → confirmation → contrat → état des lieux → restitution : rien n'est oublié, tout est tracé." },
      { icon: BellRing, title: "Relances planifiées", desc: "Un prospect sans réponse ? Une tâche de relance vous le rappelle au bon moment." },
    ],
    quote: { text: "Avant je perdais des demandes prises au téléphone. Maintenant tout part d'une réservation propre en 30 secondes.", author: "Sophie L.", role: "Agence de location, Lyon" },
  },
  {
    slug: "contrats-signature",
    icon: FileText,
    menuLabel: "Contrats & signature",
    seoTitle: "Contrat de location automatique & signature électronique | Junago",
    overline: "Contrats & signature électronique",
    title: "Le contrat se remplit et se signe tout seul.",
    subtitle:
      "À chaque réservation confirmée, Junago génère un contrat pré-rempli que votre client signe électroniquement — sur place ou à distance. Fini les documents Word recopiés.",
    Mockup: ContractMockup,
    helpSlug: "contrats-signature-electronique",
    benefits: [
      { icon: FileText, title: "Génération automatique", desc: "Identité du client, véhicule, dates, tarif, conditions : le contrat est prêt dès la confirmation." },
      { icon: PenLine, title: "Signature électronique", desc: "Le client signe sur smartphone, tablette ou ordinateur. Signature horodatée, exemplaire pour chaque partie." },
      { icon: Lock, title: "Valeur de preuve", desc: "Contrat archivé avec sa signature datée, rattaché au client, au véhicule et aux états des lieux." },
      { icon: Sparkles, title: "À vos couleurs", desc: "Vos informations d'entreprise alimentent automatiquement chaque document. Cohérent et professionnel." },
    ],
    quote: { text: "Mes clients signent sur leur téléphone avant même d'arriver à l'agence. On gagne 15 minutes par départ.", author: "Karim B.", role: "Loueur premium, Bordeaux" },
  },
  {
    slug: "etats-des-lieux",
    icon: Camera,
    menuLabel: "États des lieux",
    seoTitle: "État des lieux digital pour location de véhicules | Junago",
    overline: "États des lieux digitaux",
    title: "Vos états des lieux sur smartphone. Fini le papier.",
    subtitle:
      "Photos horodatées, kilométrage, niveau de carburant : réalisez l'état des lieux de départ et de retour directement depuis Junago, et comparez-les automatiquement.",
    Mockup: InspectionMockup,
    helpSlug: "contrats-signature-electronique",
    benefits: [
      { icon: Camera, title: "Photos zone par zone", desc: "Avant, arrière, côtés, intérieur : un parcours guidé pour ne rien oublier, avec photos horodatées." },
      { icon: Search, title: "Comparaison automatique", desc: "Départ et retour comparés côte à côte. Une rayure apparue ? Vous avez la preuve datée." },
      { icon: ShieldCheck, title: "Litiges désamorcés", desc: "Un dossier photo complet et opposable, attaché au contrat signé. Les contestations tombent d'elles-mêmes." },
      { icon: Clock, title: "5 minutes chrono", desc: "L'état des lieux complet se fait sur place, sur votre téléphone, en quelques minutes." },
    ],
    quote: { text: "Un client contestait une rayure. J'ai sorti les photos horodatées du départ : discussion terminée en 30 secondes.", author: "Nadia R.", role: "Loueuse utilitaires, Marseille" },
  },
  {
    slug: "flotte-entretien",
    icon: Wrench,
    menuLabel: "Flotte & entretien",
    seoTitle: "Logiciel de gestion de flotte et suivi d'entretien | Junago",
    overline: "Flotte & entretien",
    title: "Chaque véhicule sous contrôle, chaque échéance anticipée.",
    subtitle:
      "Fiches véhicules complètes, documents avec dates d'expiration, alertes automatiques pour le contrôle technique et l'assurance : votre parc n'a plus de zone d'ombre.",
    Mockup: VehicleAddMockup,
    helpSlug: "gerer-votre-flotte",
    benefits: [
      { icon: Wrench, title: "Alertes avant échéance", desc: "Contrôle technique, assurance, révision : Junago vous prévient 15, 30 ou 60 jours avant, au choix." },
      { icon: FolderCheck, title: "Documents centralisés", desc: "Carte grise, assurance, factures d'entretien : tout est stocké sur la fiche du véhicule." },
      { icon: TrendingUp, title: "Rentabilité par véhicule", desc: "Revenu, jours loués, charges : identifiez les véhicules qui rapportent et ceux qui coûtent." },
      { icon: History, title: "Historique complet", desc: "Chaque modification, document ou changement de statut est historisé dans une timeline." },
    ],
    quote: { text: "Le contrôle technique d'un Kangoo allait expirer en pleine saison. L'alerte Junago m'a sauvé la mise.", author: "Thomas G.", role: "Flotte de 22 véhicules, Nantes" },
  },
  {
    slug: "crm-clients",
    icon: Users,
    menuLabel: "CRM clients",
    seoTitle: "CRM pour loueurs de véhicules | Junago",
    overline: "CRM clients & prospects",
    title: "Vos clients vous connaissent. Connaissez-les aussi.",
    subtitle:
      "Fiches clients complètes, historique des locations, suivi des prospects avec relances : le CRM intégré transforme chaque contact en client fidèle.",
    Mockup: ClientsMockup,
    helpSlug: "crm-clients-prospects",
    benefits: [
      { icon: Users, title: "Une fiche par client", desc: "Coordonnées, permis, contrats signés, chiffre d'affaires généré : tout est là, pré-rempli pour la prochaine location." },
      { icon: Filter, title: "Prospects suivis", desc: "Créez un prospect en 10 secondes, envoyez un devis, relancez au bon moment. Converti ? Il devient client en 1 clic." },
      { icon: TrendingUp, title: "Meilleurs clients identifiés", desc: "Classez vos clients par chiffre d'affaires et soignez ceux qui recommandent votre agence." },
    ],
    quote: { text: "Un habitué rappelle, je tape son nom : permis, préférences, historique. La réservation est faite avant la fin de l'appel.", author: "Julie M.", role: "Agence familiale, Toulouse" },
  },
  {
    slug: "encaissements",
    icon: CreditCard,
    menuLabel: "Encaissements Stripe",
    seoTitle: "Encaissement en ligne pour loueurs — Stripe | Junago",
    overline: "Encaissements sécurisés",
    title: "Encaissez par carte. Dormez tranquille.",
    subtitle:
      "Vos clients paient par carte bancaire via Stripe, leader mondial du paiement sécurisé. Les fonds arrivent directement sur votre compte — aucune donnée bancaire stockée chez Junago.",
    Mockup: PaymentMockup,
    helpSlug: "paiements-securises-stripe",
    benefits: [
      { icon: CreditCard, title: "Toutes cartes, 3D-Secure", desc: "Visa, Mastercard, CB : chaque transaction est authentifiée et chiffrée par Stripe (PCI-DSS niveau 1)." },
      { icon: Banknote, title: "Virement sous 48h", desc: "Les encaissements sont reversés automatiquement sur votre IBAN. Intégralité du montant, sans commission." },
      { icon: TrendingUp, title: "Revenus suivis en temps réel", desc: "Chaque paiement est réconcilié avec sa réservation. CA par période, par véhicule, évolution mensuelle." },
      { icon: ShieldCheck, title: "Optionnel et flexible", desc: "Vous préférez virement ou espèces ? Enregistrez la réservation normalement, les statistiques suivent." },
    ],
    quote: { text: "Le paiement par carte a fait disparaître les impayés. Et le virement arrive sans que j'y pense.", author: "David P.", role: "Loueur de vans aménagés, Biarritz" },
  },
  {
    slug: "revenus-facturation",
    icon: Receipt,
    menuLabel: "Revenus & facturation",
    seoTitle: "Cockpit financier pour loueurs : revenus, factures, avoirs, cautions, export comptable | Junago",
    overline: "Revenus & facturation",
    title: "Votre cockpit financier, du devis à l'export comptable.",
    subtitle:
      "Synthèse des revenus, encaissements par canal, cautions, factures et avoirs conformes, suivi des dépenses et rentabilité par véhicule : toute votre finance au même endroit, exportable pour votre comptable en un clic.",
    Mockup: RevenueMockup,
    helpSlug: "revenus-facturation-export",
    benefits: [
      { icon: Receipt, title: "Factures & avoirs conformes", desc: "Numérotation légale continue, factures générées depuis la réservation ou libres, avoirs en un clic, TVA gérée automatiquement." },
      { icon: ShieldCheck, title: "Cautions maîtrisées", desc: "Empreinte bancaire, capture partielle ou totale en cas de dommage, libération automatique : chaque caution est tracée avec sa réservation." },
      { icon: TrendingUp, title: "Rentabilité par véhicule", desc: "Revenus, coûts d'entretien, dépenses : Junago calcule la marge réelle de chaque véhicule pour guider vos achats et vos reventes." },
      { icon: FolderCheck, title: "Export comptable mensuel", desc: "Un CSV propre par période avec toutes vos écritures : votre comptable vous remerciera." },
    ],
    quote: { text: "Mon expert-comptable reçoit un export propre chaque mois. Avant, je passais un dimanche entier sur Excel.", author: "Nathalie R.", role: "Loueuse, Lyon" },
  },
  {
    slug: "parametrages",
    icon: Settings2,
    menuLabel: "Paramétrages",
    seoTitle: "Un logiciel de location qui s'adapte à vous | Junago",
    overline: "Paramétrages sur mesure",
    title: "Junago s'adapte à votre façon de travailler.",
    subtitle:
      "Catégories de véhicules, statuts colorés, champs personnalisés, seuils d'alerte : configurez la plateforme pour qu'elle épouse vos process — pas l'inverse.",
    Mockup: SignatureMockup,
    helpSlug: "parametrages-personnalisation",
    benefits: [
      { icon: Palette, title: "Statuts & couleurs personnalisés", desc: "Disponible, en entretien, immobilisé : créez vos statuts avec leur code couleur, visibles partout." },
      { icon: Settings2, title: "Champs personnalisés", desc: "Numéro de vol, lieu de livraison, siège bébé : ajoutez vos propres champs sur les réservations." },
      { icon: AlarmClock, title: "Alertes configurables", desc: "Choisissez le délai de prévenance pour l'expiration des documents : 15, 30 ou 60 jours." },
      { icon: LayoutGrid, title: "Catégories réorganisables", desc: "Citadine, SUV, utilitaire, prestige : organisez vos catégories par glisser-déposer." },
    ],
    quote: { text: "J'ai créé un statut « Préparation esthétique » avec sa couleur. Mon équipe sait instantanément où en est chaque véhicule.", author: "Romain V.", role: "Location prestige, Cannes" },
  },
  {
    slug: "equipe-securite",
    icon: UserCog,
    menuLabel: "Équipe, rôles & sécurité",
    seoTitle: "Gérez votre équipe avec rôles et permissions : accès par agence, journal d'activité | Junago",
    overline: "Équipe & sécurité",
    title: "Chaque collaborateur voit exactement ce qu'il doit voir.",
    subtitle:
      "Créez des rôles sur mesure (agent de comptoir, préparateur, comptable…), limitez un collaborateur à une agence, suivez chaque action dans le journal d'activité. Vos données restent protégées, votre équipe reste efficace.",
    Mockup: ManagementMockup,
    helpSlug: "equipe-roles-permissions",
    benefits: [
      { icon: UserCog, title: "Rôles & permissions fins", desc: "Lecture, création, modification, suppression, export : chaque module se règle indépendamment. Le rôle « Agent » ne verra jamais vos finances." },
      { icon: Filter, title: "Restriction par agence", desc: "Un collaborateur rattaché à une agence ne voit que les réservations de son site — sur l'écran comme dans l'API." },
      { icon: History, title: "Journal d'activité complet", desc: "Qui a modifié ce tarif ? Qui a annulé cette réservation ? Chaque action est horodatée avec son auteur et son adresse IP." },
      { icon: ShieldCheck, title: "Sécurité du compte", desc: "Verrouillage automatique après 5 tentatives de connexion échouées, mots de passe chiffrés, sessions sécurisées." },
    ],
    quote: { text: "Mes deux agents gèrent le comptoir sans jamais voir mon chiffre d'affaires. Exactement ce qu'il me fallait.", author: "Stéphane M.", role: "2 agences, Bordeaux" },
  },
  {
    slug: "emails-automatiques",
    icon: MailCheck,
    menuLabel: "Emails automatiques",
    seoTitle: "Emails automatiques de location : confirmation, rappels, modèles personnalisables | Junago",
    overline: "Emails automatiques",
    title: "Vos clients sont informés. Vous n'avez rien tapé.",
    subtitle:
      "Confirmation de réservation, rappel avant départ, envoi du contrat, du devis ou de la facture : Junago écrit et envoie les bons emails au bon moment, avec vos couleurs et votre signature. Chaque modèle est personnalisable.",
    Mockup: ContractMockup,
    helpSlug: "emails-automatiques-modeles",
    benefits: [
      { icon: MailCheck, title: "Automatisations par statut", desc: "Réservation confirmée, contrat prêt à signer, retour du véhicule : chaque étape déclenche l'email adapté, automatiquement." },
      { icon: Palette, title: "Modèles à votre marque", desc: "Logo, couleurs, textes : personnalisez chaque modèle avec des variables dynamiques (client, véhicule, dates, montants) et prévisualisez avant d'activer." },
      { icon: FileCheck, title: "Documents joints", desc: "Devis, contrat, facture, lien de signature ou d'acceptation : les bons documents accompagnent chaque email, sans manipulation." },
      { icon: History, title: "Historique des envois", desc: "Chaque email envoyé est tracé et consultable depuis la réservation : vous savez toujours ce que le client a reçu." },
    ],
    quote: { text: "Le rappel automatique 48h avant le départ a divisé mes no-shows par trois.", author: "Aline T.", role: "Loueuse, Marseille" },
  },
];

export const getFeaturePage = (slug) => FEATURE_PAGES.find((f) => f.slug === slug);
